import { ReactNode, useCallback } from 'react';

import { Enum } from '~/data/enums';
import { ProposalConfigSelectorOption } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalConfigSelector/types';

export type ProposalConfigSelectorProps<TConfig> = {
  selectedConfig?: TConfig;
  onSelectedConfigChange: (
    typeConfig: TConfig | undefined,
  ) => Promise<TConfig | undefined>;
  typeEnums: Enum[];
  options: TConfig[];
  currency: string;
  getNonPresentConfig: (configType: string) => TConfig;
  convertConfigToOption: (config: TConfig) => ProposalConfigSelectorOption;
  customOptionDescription?: (config: TConfig, currency: string) => ReactNode;
};

type UtilsProps<TConfig> = {
  convertConfigToOption: (config: TConfig) => ProposalConfigSelectorOption;
  getNonPresentConfig: (type: string, id?: string) => TConfig;
};

export type GenericProposalConfigUtils<TConfig> = UtilsProps<TConfig> & {
  findConfigById: (configs: TConfig[], configId: string) => TConfig | undefined;
  findConfigByType: (configs: TConfig[], type: string) => TConfig | undefined;
  getConfigIds: (config: TConfig[]) => string[];
  getConfigTypes: (configs: TConfig[]) => string[];
  getDefaultConfigs: (props: {
    proposalConfigs: TConfig[];
    proposalConfigType?: string;
    customResolveSelectedConfig?: (config: TConfig) => boolean;
  }) => { options: TConfig[]; selected?: TConfig };
  getFirstConfig: (configs: TConfig[]) => TConfig | undefined;
  matchByConfigId: (config1?: TConfig, config2?: TConfig) => boolean;
  matchByConfigType: (config1?: TConfig, config2?: TConfig) => boolean;
};
export const useGenericProposalConfigUtils = <TConfig>(
  props: UtilsProps<TConfig>,
): GenericProposalConfigUtils<TConfig> => {
  const { convertConfigToOption, getNonPresentConfig } = props;

  const getOption = convertConfigToOption;
  const getOptionOrUndefined = useCallback(
    (config?: TConfig) => (!!config ? getOption(config) : undefined),
    [getOption],
  );

  const getConfigIds = useCallback(
    (config: TConfig[]): string[] => config.map((c) => getOption(c).id),
    [getOption],
  );

  const findConfigById = useCallback(
    (configs: TConfig[], configId: string): TConfig | undefined =>
      configs.find((c) => getOption(c).id === configId),
    [getOption],
  );

  const getConfigTypes = useCallback(
    (configs: TConfig[]): string[] => configs.map((c) => getOption(c).type),
    [getOption],
  );

  const findConfigByType = useCallback(
    (configs: TConfig[], type: string): TConfig | undefined =>
      configs.find((c) => getOption(c).type === type),
    [getOption],
  );

  const matchByConfigId = useCallback(
    (config1?: TConfig, config2?: TConfig) =>
      getOptionOrUndefined(config1)?.id === getOptionOrUndefined(config2)?.id,
    [getOptionOrUndefined],
  );

  const matchByConfigType = useCallback(
    (config1?: TConfig, config2?: TConfig) =>
      getOptionOrUndefined(config1)?.type ===
      getOptionOrUndefined(config2)?.type,
    [getOptionOrUndefined],
  );

  const resolveOptions = useCallback(
    (props: {
      proposalConfigs: TConfig[];
      configType?: string;
      nonPresentId?: string;
    }): TConfig[] => {
      const { proposalConfigs, configType, nonPresentId } = props;

      const foundConfig = !!configType
        ? findConfigByType(proposalConfigs, configType)
        : undefined;

      return !foundConfig && !!configType
        ? [getNonPresentConfig(configType, nonPresentId), ...proposalConfigs]
        : proposalConfigs;
    },
    [findConfigByType, getNonPresentConfig],
  );

  const findSelectedConfig = useCallback(
    (
      configs: TConfig[],
      type: string,
      customResolveSelectedConfig?: (config: TConfig) => boolean,
    ): TConfig | undefined => {
      if (!!customResolveSelectedConfig) {
        const configsMatchByType = configs.filter(
          (c) => getOption(c).type === type,
        );

        return configsMatchByType.length > 0
          ? configsMatchByType.find(customResolveSelectedConfig) ??
              configsMatchByType[0]
          : undefined;
      }

      return findConfigByType(configs, type);
    },
    [getOption, findConfigByType],
  );

  const getFirstConfig = useCallback(
    (configs: TConfig[]) => (configs.length > 0 ? configs[0] : undefined),
    [],
  );

  const getDefaultConfigs = useCallback(
    (props: {
      proposalConfigs: TConfig[];
      proposalConfigType?: string;
      customResolveSelectedConfig?: (config: TConfig) => boolean;
    }): { options: TConfig[]; selected?: TConfig } => {
      const {
        proposalConfigs,
        proposalConfigType,
        customResolveSelectedConfig,
      } = props;

      const options = resolveOptions({
        proposalConfigs,
        configType: proposalConfigType,
      });

      const onlyTypeConfig =
        options.length === 1 ? getFirstConfig(options) : undefined;

      const defaultSelected = !!proposalConfigType
        ? findSelectedConfig(
            options,
            proposalConfigType,
            customResolveSelectedConfig,
          ) || onlyTypeConfig
        : onlyTypeConfig;

      return {
        options,
        selected: defaultSelected,
      };
    },
    [findSelectedConfig, getFirstConfig, resolveOptions],
  );

  return {
    findConfigById,
    findConfigByType,
    getConfigIds,
    getConfigTypes,
    getDefaultConfigs,
    getFirstConfig,
    getNonPresentConfig,
    matchByConfigId,
    matchByConfigType,
    convertConfigToOption,
  };
};
